import React, { useEffect, useState, Suspense } from 'react';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import { LinkTag, useHelmet } from '@thd-nucleus/thd-helmet';
import { MediaHotspots } from '@thd-olt-component-react/product-hotspots';
import Helper from './Helper';
import { componentReady, mediaClick, thumbnailClick } from '../publisher';
import { dataModel, hotspotsMediaDataModel } from './dataModel';
import { mediaGalleryDesktopPropTypes } from './MediaGalleryPropTypes';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';
import { StaticImageGrid } from './subcomponents/StaticImageGrid';
import { DEFAULT_VISIBLE_TILE_COUNT, HERO_IMAGE_SIZE } from './subcomponents/StaticImageGrid/constants';
import { buildSrcSet, isPreloadableImageUrl, SIZES_HERO, getMediaStillUrl } from './common/imageUrlUtils';

const MediaGalleryOverlayTabs = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay-tabs" */'./MediaGalleryComponents/MediaGalleryOverlayTabs')
);

const getOverlayMediaType = (media) => {
  if (Helper.isVideo({ media })) { return 'VIDEO'; }
  if (Helper.isImage360({ media })) { return 'THREESIXTY'; }
  if (Helper.isAR({ media })) { return 'AR'; }
  if (Helper.isHotspot({ media })) { return 'HOTSPOTS'; }
  return 'IMAGE';
};

/**
 * Emits a head preload for the hero so the LCP image is discoverable by the preload
 * scanner instead of waiting on hydration plus the product query.
 */
const heroPreloadTags = ({ data }) => {
  const { heroUrl, heroSrcSet } = data || {};
  if (!isPreloadableImageUrl(heroUrl)) { return []; }

  const attributes = {
    id: 'media-gallery-hero',
    rel: 'preload',
    as: 'image',
    fetchpriority: 'high',
    href: heroUrl
  };

  if (heroSrcSet) {
    attributes.imagesrcset = heroSrcSet;
    attributes.imagesizes = SIZES_HERO;
  }

  return [new LinkTag(attributes)];
};

/**
 * Media grid: a static two-column image grid.
 *
 */
export const MediaGrid = ({
  collectionId = null,
  configId = undefined,
  hideProductImagesLabel = false,
  itemId = null,
  mainImageFetchPriority = 'high',
  overrideData = null,
  showLoader = false,
  visibleTileCount = DEFAULT_VISIBLE_TILE_COUNT,
  onShowMore = null
}) => {
  const storeId = useStoreId();
  const [overlayOpened, setOverlayOpened] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, []);

  const productData = useDataModel('product', {
    variables: {
      itemId,
      configId,
      storeId,
      skipPaintDetails: !configId
    },
    skip: !itemId || !!overrideData
  });

  const collectionData = useDataModel('collection', {
    variables: { collectionId, storeId },
    skip: !collectionId || !!overrideData
  });

  const { displayedMedia = {}, ...apiResults } = Helper.getDisplayedMedia({
    isMobile: false,
    itemId,
    productData,
    collectionId,
    collectionData,
    overrideData,
    placeholder: showLoader && <MediaGalleryLoader isMobile={false} />
  });

  const { main = [] } = displayedMedia;
  const heroUrl = mainImageFetchPriority === 'high' ? getMediaStillUrl(main[0]) : null;
  const heroSrcSet = buildSrcSet(heroUrl, HERO_IMAGE_SIZE);

  useHelmet(
    'media-grid',
    { data: { heroUrl, heroSrcSet } },
    heroPreloadTags,
    [heroUrl, heroSrcSet]
  );

  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }

  const { product } = productData?.data || {};
  const { collection } = collectionData?.data || {};

  const handleMediaClick = (media, index) => {
    // Same payloads MediaGalleryDesktop emits when a thumbnail opens the overlay
    thumbnailClick({ primaryCategory: 'overlay', eventName: 'expanded-view' });
    const mediaType = Helper.getMediaType(media);
    mediaClick({
      section: 'media',
      component: 'thumbnails',
      target: mediaType,
      position: index + 1,
      guid: mediaType === 'image' ? Helper.getImageGuid(media) : 'n/a'
    });

    setSelectedMediaIndex(index);
    setOverlayOpened(true);
  };

  const handleOverlayClose = () => setOverlayOpened(false);

  return (
    <>
      <StaticImageGrid
        heroMedia={main}
        tileMedia={main}
        identifiers={product?.identifiers}
        overrideData={overrideData}
        hideProductImagesLabel={hideProductImagesLabel}
        visibleTileCount={visibleTileCount}
        heroFetchPriority={mainImageFetchPriority}
        onShowMore={onShowMore}
        onMediaClick={handleMediaClick}
      />
      {overlayOpened && (
        <Suspense fallback={<div />}>
          <MediaGalleryOverlayTabs
            handleClose={handleOverlayClose}
            overlayOpened={overlayOpened}
            collection={collection}
            media={displayedMedia}
            product={product}
            mainAsset={main[selectedMediaIndex]}
            thumbnails={displayedMedia.thumbnails || []}
            overrideData={overrideData}
            hideProductImagesLabel={hideProductImagesLabel}
            itemId={itemId}
            videoAnalyticsData={{ position: String(selectedMediaIndex + 1) }}
            initialType={getOverlayMediaType(main[selectedMediaIndex])}
            initialId={main[selectedMediaIndex]?.id}
          />
        </Suspense>
      )}
    </>
  );
};

MediaGrid.displayName = 'MediaGrid';

MediaGrid.propTypes = mediaGalleryDesktopPropTypes;

MediaGrid.dataModel = extend({}, hotspotsMediaDataModel, dataModel, MediaHotspots);
